import { type SessionSummary } from 'conversational-search-client';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { type JSX, type RefObject } from 'react';
import { twMerge } from 'tailwind-merge';

import { Text } from '@videoblocks/design-system';
import { useCloseOnOutsideClick } from '@videoblocks/design-system/hooks';

function formatRelativeTime(dateString: string): string {
    const now = new Date();
    const date = new Date(dateString);
    const diffMs = now.getTime() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMin < 1) return 'Latest';
    if (diffMin < 60) return `${diffMin} min ago`;
    if (diffHours < 24) return `${diffHours} hr ago`;
    if (diffDays === 1) return '1 day ago';
    return `${diffDays} days ago`;
}

export function SessionDropdownMenu({
    isOpen,
    setIsOpen,
    sessions,
    containerRef,
    positionClassName = 'top-full left-4 mt-1',
}: {
    isOpen: boolean;
    setIsOpen: (open: boolean) => void;
    sessions: SessionSummary[];
    containerRef: RefObject<HTMLElement | null>;
    positionClassName?: string;
}): JSX.Element | null {
    const router = useRouter();

    useCloseOnOutsideClick(isOpen, setIsOpen, containerRef);

    // Drop sessions that never got an assistant response (e.g. the Claude API
    // errored out): messages alternate user/assistant, so a real exchange is >= 2.
    const answeredSessions = sessions.filter((session) => session.messageCount >= 2);

    // Sort by lastActivity descending so the most recent is first
    const sortedSessions = [...answeredSessions].sort(
        (a, b) => new Date(b.lastActivity).getTime() - new Date(a.lastActivity).getTime(),
    );

    if (!isOpen || !sortedSessions.length) return null;

    return (
        <div
            className={twMerge(
                'absolute z-20 flex max-h-[350px] w-[343px] flex-col overflow-hidden rounded-lg border border-gray-100 bg-white shadow-[0px_1px_4px_rgba(18,18,19,0.2)]',
                positionClassName,
            )}
        >
            <div className="shrink-0 border-b border-gray-200 px-4 py-2">
                <Text fontSize="lg" fontWeight="semibold" className="text-gray-900">
                    Recent
                </Text>
                <Text fontSize="sm" fontWeight="regular" className="text-gray-600">
                    Latest 50 conversations
                </Text>
            </div>

            <ul className="min-h-0 flex-1 overflow-y-auto px-2 py-2">
                {sortedSessions.map((session, index) => (
                    <li key={session.id}>
                        <Link
                            className="flex w-full flex-col gap-0.5 rounded-lg px-2 py-2 text-left hover:bg-gray-100 transition-colors cursor-pointer"
                            onMouseEnter={() => router.prefetch(`/ai/conv-search/${session.id}`)}
                            href={`/ai/conv-search/${session.id}`}
                            onClick={() => {
                                setIsOpen(false);
                            }}
                        >
                            <Text fontSize="sm" fontWeight="regular" className="truncate text-gray-900">
                                {session.name}
                            </Text>
                            <Text fontSize="xs" fontWeight="regular" className="text-gray-600">
                                {index === 0 ? 'Latest' : formatRelativeTime(session.lastActivity)}
                            </Text>
                        </Link>
                    </li>
                ))}
            </ul>
        </div>
    );
}
